import { Link } from 'react-router';

import { useAppSelector } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';

import {
  FIRST_HAIRCARE_QUESTION_ROUTE,
  FIRST_SKINCARE_QUESTION_ROUTE,
} from 'Apps/Consultation/constants';

import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

import { cartUrlFromCategory } from 'constants/cart';
import { productsCategories } from 'constants/products';

import * as ctaContent from 'assets/content/consultation/cta';

import { getIsAuthenticated, getIsAuthenticating } from 'dux/auth/selectors';
import {
  getFirstHaircareConsultationQuestionRoute,
  getFirstSkincareConsultationQuestionRoute,
} from 'dux/consultation/selectors';
import * as userSelectors from 'dux/user/selectors';

const StyledTypography = styled(Typography)`
  font-weight: bold;
`;

type ComputeCTAPropsParams = {
  isAuthenticated: boolean;
  category: string;
  hasDraftHaircareConsultation: boolean;
  hasCompletedHaircareConsultation: boolean;
  hasDraftSkincareConsultation: boolean;
  hasCompletedSkincareConsultation: boolean;
  firstHaircareConsultationQuestionRoute: string;
  firstSkincareConsultationQuestionRoute: string;
};

const computeCTAProps = ({
  isAuthenticated,
  category,
  hasDraftHaircareConsultation,
  hasCompletedHaircareConsultation,
  hasDraftSkincareConsultation,
  hasCompletedSkincareConsultation,
  firstHaircareConsultationQuestionRoute,
  firstSkincareConsultationQuestionRoute,
}: ComputeCTAPropsParams) => {
  if (isAuthenticated) {
    if (
      (category === productsCategories.HAIRCARE && hasDraftHaircareConsultation) ||
      (category === productsCategories.SKINCARE && hasDraftSkincareConsultation)
    ) {
      return {
        to:
          category === productsCategories.SKINCARE
            ? firstSkincareConsultationQuestionRoute
            : firstHaircareConsultationQuestionRoute,
        label: ctaContent.oneConsultationStarted,
        dataClick: `finish-consultation-${category}`,
      } as const;
    }
    if (
      (category === productsCategories.HAIRCARE && hasCompletedHaircareConsultation) ||
      (category === productsCategories.SKINCARE && hasCompletedSkincareConsultation)
    ) {
      return {
        to: cartUrlFromCategory[category],
        label: ctaContent.abandonedCart(),
        dataClick: `${category}-cart`,
      } as const;
    }
  }

  return {
    to:
      category === productsCategories.HAIRCARE
        ? FIRST_HAIRCARE_QUESTION_ROUTE
        : FIRST_SKINCARE_QUESTION_ROUTE,
    label: ctaContent.newUserPDP,
    dataClick: `consultation-${category}`,
  } as const;
};

type IngredientsCTAProps = {
  dataFrom: string;
  /**
   * @deprecated
   * please use variant instead.
   */
  theme?: 'underlined' | 'sorbet';
  variant?: 'underlined' | 'sorbet';
  category: string;
};

export const IngredientsCTA = ({
  dataFrom = 'ingredients-cta',
  theme = 'sorbet',
  variant = theme,
  category,
}: IngredientsCTAProps) => {
  const hasDraftHaircareConsultation =
    useAppSelector(userSelectors.getHasDraftHaircareConsultation) ?? false;
  const hasCompletedHaircareConsultation =
    useAppSelector(userSelectors.getHasCompletedHaircareConsultation) ?? false;
  const hasDraftSkincareConsultation =
    useAppSelector(userSelectors.getHasDraftSkincareConsultation) ?? false;
  const hasCompletedSkincareConsultation =
    useAppSelector(userSelectors.getHasCompletedSkincareConsultation) ?? false;
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const isAuthenticating = useAppSelector(getIsAuthenticating);
  const firstHaircareConsultationQuestionRoute: string = useAppSelector(
    getFirstHaircareConsultationQuestionRoute,
  );
  const firstSkincareConsultationQuestionRoute: string = useAppSelector(
    getFirstSkincareConsultationQuestionRoute,
  );

  if (isAuthenticating) {
    return null;
  }

  const params = computeCTAProps({
    isAuthenticated,
    category,
    hasDraftHaircareConsultation,
    hasCompletedHaircareConsultation,
    hasDraftSkincareConsultation,
    hasCompletedSkincareConsultation,
    firstHaircareConsultationQuestionRoute,
    firstSkincareConsultationQuestionRoute,
  });

  return (
    <Button
      Component={Link}
      data-click={params.dataClick}
      data-from={dataFrom}
      to={params.to}
      variant={variant ?? theme}
    >
      <StyledTypography markupName="h2" variant="mono2">
        {params.label}
      </StyledTypography>
    </Button>
  );
};
